import { useState } from 'react';
import {
  ITagField,
  ILabelValueField,
  isLabelValueField,
  EnhancedFieldCsvRow,
  onEnhanceClickHandler,
  IProductEnhancerField,
  EnhancedFieldsMapState,
  isProductEnhancerField,
  onReEnhanceClickHandler,
  onTagRemoveClickHandler,
  onTextAreaChangeHandler,
  onLabelValueChangeHandler
} from '../../types';
import {
  ENHANCED_FIELDS_INITIAL_STATE,
  ORIGINAL_FIELDS_INITIAL_STATE,
  ENHANCED_FIELDS_ENHANCED_STATE,
  ENHANCED_FIELDS_RE_ENHANCED_STATE
} from '../../config';
import { generateEnhancedFieldsCSV } from './helpers';

export const useProductEnhancer = () => {
  // ! state
  const [originalFields] = useState<IProductEnhancerField[]>(
    ORIGINAL_FIELDS_INITIAL_STATE
  );
  const [enhancedFields, setEnhancedFields] = useState<EnhancedFieldsMapState>(
    ENHANCED_FIELDS_INITIAL_STATE
  );

  const [enhancedCsvData, setEnhancedCsvData] = useState<EnhancedFieldCsvRow[]>(
    []
  );

  const [enhanceLoadingElement, setEnhanceLoadingElement] = useState<
    IProductEnhancerField['id'] | 'all' | null
  >(null);

  // ! helpers
  const fakeLoadingData = async (
    fieldToLoad: IProductEnhancerField['id'] | 'all'
  ) => {
    setEnhanceLoadingElement(fieldToLoad);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setEnhanceLoadingElement(null);
  };

  // ! handlers
  const onExportClick = () => {
    const csvData = generateEnhancedFieldsCSV(originalFields, enhancedFields);
    setEnhancedCsvData(csvData);
  };

  const onEnhanceAllAlick = async () => {
    setEnhancedFields(ENHANCED_FIELDS_ENHANCED_STATE);
    await fakeLoadingData('all');
  };

  const onEnhanceClick: onEnhanceClickHandler = async fieldId => {
    const updatedField = ENHANCED_FIELDS_ENHANCED_STATE[fieldId];

    setEnhancedFields(prev => ({
      ...prev,
      [fieldId]: updatedField
    }));
    await fakeLoadingData(fieldId);
  };

  const onReEnhanceClick: onReEnhanceClickHandler = async fieldId => {
    setEnhancedFields(prev => ({
      ...prev,
      [fieldId]: ENHANCED_FIELDS_RE_ENHANCED_STATE[fieldId]
    }));
    await fakeLoadingData(fieldId);
  };

  const onTagRemoveClick: onTagRemoveClickHandler = (fieldId, tag) => {
    setEnhancedFields(prev => {
      const field = prev[fieldId];
      if (!isProductEnhancerField(field)) return prev;
      const updatedTags = (field as ITagField).data.filter(
        tagItem => tagItem !== tag
      );

      return {
        ...prev,
        [field.id]: {
          ...field,
          data: updatedTags.length ? updatedTags : undefined
        }
      };
    });
  };

  const onTextAreaChange: onTextAreaChangeHandler = (fieldId, value) => {
    setEnhancedFields(prevState => ({
      ...prevState,
      [fieldId]: {
        ...prevState[fieldId],
        data: value
      }
    }));
  };

  const onLabelValueChange: onLabelValueChangeHandler = (
    fieldId,
    itemIndex,
    value
  ) => {
    setEnhancedFields(prev => {
      const field = prev[fieldId];
      if (!isProductEnhancerField(field) || !isLabelValueField(field)) {
        return prev;
      }

      const updatedFieldData: ILabelValueField['data'] = [...field.data];
      updatedFieldData[itemIndex] = { ...updatedFieldData[itemIndex], value };

      const updatedField: ILabelValueField = {
        ...field,
        data: updatedFieldData
      };

      return {
        ...prev,
        [fieldId]: updatedField
      };
    });
  };

  // ! return
  return {
    originalFields,
    enhancedFields,
    onExportClick,
    onEnhanceClick,
    enhancedCsvData,
    onReEnhanceClick,
    onTagRemoveClick,
    onTextAreaChange,
    onEnhanceAllAlick,
    onLabelValueChange,
    enhanceLoadingElement
  };
};
